<script lang="ts">
  /**
   * Ctrl/⌘+K command palette. Static command list today — easy to extend
   * with recent builds / live search later. Opens on the keyboard
   * shortcut and the topbar trigger. Filters by fuzzy substring on label.
   */
  import { fly, fade } from 'svelte/transition';
  import { navigate } from '$router/router.svelte';
  import { session } from '$domain/session.svelte';
  import { setTheme, setAccent, setDensity } from '$domain/settings.svelte';

  let open = $state(false);
  let query = $state('');
  let activeIdx = $state(0);
  let inputEl: HTMLInputElement | null = $state(null);

  interface Cmd {
    id: string;
    label: string;
    hint?: string;
    group: string;
    run: () => void;
    /** Hide command unless predicate returns true. */
    show?: () => boolean;
  }

  const commands: Cmd[] = [
    { id: 'go-home', label: 'Go to Home', group: 'navigate', run: () => navigate('/') },
    { id: 'go-wizard', label: 'New build', group: 'navigate', run: () => navigate('/wizard') },
    {
      id: 'go-inventory',
      label: 'My inventory',
      group: 'navigate',
      run: () => navigate('/inventory'),
    },
    {
      id: 'go-admin',
      label: 'Admin dashboard',
      group: 'navigate',
      run: () => navigate('/admin'),
      show: () => session.isAdmin,
    },
    {
      id: 'theme-dark',
      label: 'Theme: Dark',
      group: 'theme',
      run: () => setTheme('dark'),
    },
    {
      id: 'theme-light',
      label: 'Theme: Light',
      group: 'theme',
      run: () => setTheme('light'),
    },
    {
      id: 'theme-auto',
      label: 'Theme: Auto',
      group: 'theme',
      run: () => setTheme('auto'),
    },
    {
      id: 'accent-solar',
      label: 'Accent: Solar',
      group: 'accent',
      run: () => setAccent('solar'),
    },
    { id: 'accent-arc', label: 'Accent: Arc', group: 'accent', run: () => setAccent('arc') },
    { id: 'accent-void', label: 'Accent: Void', group: 'accent', run: () => setAccent('void') },
    {
      id: 'accent-stasis',
      label: 'Accent: Stasis',
      group: 'accent',
      run: () => setAccent('stasis'),
    },
    {
      id: 'density-compact',
      label: 'Density: Compact',
      group: 'density',
      run: () => setDensity('compact'),
    },
    {
      id: 'density-cozy',
      label: 'Density: Cozy',
      group: 'density',
      run: () => setDensity('cozy'),
    },
    {
      id: 'density-comf',
      label: 'Density: Comfortable',
      group: 'density',
      run: () => setDensity('comfortable'),
    },
  ];

  const visible = $derived(commands.filter((c) => !c.show || c.show()));

  /** Naive substring filter — fast enough at this scale. The first
   *  matching command takes activeIdx 0. */
  const filtered = $derived.by(() => {
    const q = query.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter((c) => c.label.toLowerCase().includes(q));
  });

  function openPalette(): void {
    open = true;
    query = '';
    activeIdx = 0;
    queueMicrotask(() => inputEl?.focus());
  }
  function closePalette(): void {
    open = false;
  }
  function runIdx(i: number): void {
    const cmd = filtered[i];
    if (!cmd) return;
    cmd.run();
    closePalette();
  }

  function onKey(e: KeyboardEvent): void {
    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl && (e.key === 'k' || e.key === 'K')) {
      e.preventDefault();
      if (open) closePalette();
      else openPalette();
      return;
    }
    if (!open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closePalette();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIdx = Math.min(filtered.length - 1, activeIdx + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIdx = Math.max(0, activeIdx - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runIdx(activeIdx);
    }
  }

  // Reset activeIdx when the filter changes so the highlight doesn't
  // point at a removed row.
  $effect(() => {
    void filtered.length;
    activeIdx = 0;
  });

  $effect(() => {
    if (typeof document === 'undefined') return;
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  // Expose openPalette globally so the topbar trigger can invoke it
  // without a parent-child binding.
  if (typeof window !== 'undefined') {
    (window as unknown as { __cryptarchOpenPalette?: () => void }).__cryptarchOpenPalette =
      openPalette;
  }
</script>

{#if open}
  <div
    class="cp-backdrop"
    transition:fade={{ duration: 120 }}
    role="presentation"
    onclick={closePalette}
    onkeydown={(e) => e.key === 'Escape' && closePalette()}
  ></div>
  <div
    class="cp"
    role="dialog"
    aria-label="command palette"
    transition:fly={{ y: -10, duration: 160 }}
  >
    <input
      bind:this={inputEl}
      bind:value={query}
      type="text"
      placeholder="Type a command…"
      aria-label="search commands"
    />
    <ul class="cp-list">
      {#each filtered as cmd, i (cmd.id)}
        <li
          class:active={i === activeIdx}
          onclick={() => runIdx(i)}
          onkeydown={(e) => e.key === 'Enter' && runIdx(i)}
          onmouseenter={() => (activeIdx = i)}
          role="option"
          tabindex="-1"
          aria-selected={i === activeIdx}
        >
          <span class="cp-group">{cmd.group}</span>
          <span class="cp-label">{cmd.label}</span>
          {#if cmd.hint}<span class="cp-hint">{cmd.hint}</span>{/if}
        </li>
      {/each}
      {#if filtered.length === 0}
        <li class="cp-empty">no matching commands</li>
      {/if}
    </ul>
    <footer>
      <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
      <span><kbd>enter</kbd> select</span>
      <span><kbd>esc</kbd> close</span>
    </footer>
  </div>
{/if}

<style>
  .cp-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(3px);
    z-index: var(--z-modal);
  }
  .cp {
    position: fixed;
    top: 12vh;
    left: 50%;
    transform: translateX(-50%);
    width: min(560px, 92vw);
    background: var(--bg-elev-1);
    border: 1px solid var(--border);
    border-radius: var(--radius-3);
    box-shadow: 0 30px 60px -20px rgba(0, 0, 0, 0.6);
    z-index: calc(var(--z-modal) + 1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .cp input {
    background: transparent;
    border: 0;
    border-bottom: 1px solid var(--border);
    color: var(--fg);
    font-family: var(--font-ui);
    font-size: var(--text-lg);
    padding: var(--space-4) var(--space-5);
    outline: none;
  }
  .cp input::placeholder {
    color: var(--fg-subtle);
  }
  .cp-list {
    list-style: none;
    margin: 0;
    padding: var(--space-2);
    max-height: 50vh;
    overflow-y: auto;
  }
  .cp-list li {
    display: grid;
    grid-template-columns: 80px 1fr auto;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-2);
    cursor: pointer;
    font-size: var(--text-sm);
  }
  .cp-list li.active {
    background: var(--accent-soft);
  }
  .cp-group {
    color: var(--fg-subtle);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
  .cp-label {
    color: var(--fg);
  }
  .cp-hint {
    color: var(--fg-subtle);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
  }
  .cp-empty {
    color: var(--fg-subtle);
    text-align: center;
    padding: var(--space-4);
    font-style: italic;
  }
  footer {
    display: flex;
    gap: var(--space-4);
    justify-content: center;
    padding: var(--space-2) var(--space-4);
    border-top: 1px solid var(--border);
    color: var(--fg-subtle);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
  }
  kbd {
    background: var(--bg-elev-3);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 1px 5px;
    font-size: 10px;
    margin: 0 2px;
  }
</style>
